import filterByProductType from 'util/filter-by-product-type';

import type React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { DIRECT_CALL, LAST_SESSION } from 'const/constant';
import { FORWARDING } from 'const/session-storage';
import isActiveSession from 'data/is-active-session';
import type { Product } from 'types';

import config from '../config/config';
import getParameterByName from '../util/get-parameter-by-name';
import hideWidget from '../util/hide-widget';
import isSdkMode from '../util/is-sdk-mode';
import loadInitialProduct from '../util/load-initial-product';
import showWidget from '../util/show-widget';
import validateWorkingHours from '../util/validate-working-hours';

import { ChatWindowOpenStyle } from './app-window-style';
import { StoreContext, useStoreDetails } from './store-context';

interface ICallContext {
  product?: Product;
  directCallEnabled: boolean;
  setDirectCallEnabled: (value: boolean) => void;
  productUrl?: string;
  agent?: string;
  isStoreOpen: boolean;
  team: string;
  chatWindowOpenStyle: ChatWindowOpenStyle;
  rejoinSessionId?: string;
  isActiveLastSession: boolean;
  forwarding: boolean;
  setForwarding: Dispatch<SetStateAction<boolean>>;
  productType?: string;
  currentUrl: string;
  setProduct: Dispatch<SetStateAction<Product | undefined>>;
}

const CallContext = createContext<ICallContext>({} as ICallContext);

const CallContextProvider: React.FC<React.ReactNode> = ({ children }) => {
  const { setWidgetOption, setSplashScreen, setMenu } = useContext(StoreContext);
  const { categoryWidgetOptions, categorySplashScreens, categoryMenus } = useStoreDetails();
  const [product, setProduct] = useState<Product>();
  const [productType, setProductType] = useState<string>();
  const [directCallEnabled, setDirectCallEnabled] = useState<boolean>(false);
  const [forwarding, setForwarding] = useState<boolean>(false);
  const [productUrl, setProductUrl] = useState<string>();
  const [agent, setAgent] = useState<string>();
  const [isStoreOpen, setStoreOpen] = useState<boolean>(false);
  const [team, setTeam] = useState<string>('default');
  const [chatWindowOpenStyle, setChatWindowOpenStyle] = useState<ChatWindowOpenStyle>(ChatWindowOpenStyle.MODEL);
  const { currency, widgetOption, id, connection, productConf, workingHours, callRejoin, currencyConversionRate } =
    useStoreDetails();
  const [rejoinSessionId, setRejoinSessionId] = useState<string>();
  const [isActiveLastSession, setActiveLastSession] = useState<boolean>(false);
  const [currentUrl, setCurrentUrl] = useState<string>(window.location.href);

  window.conferwith.onchangeUrl = function (url: string) {
    console.info('on URL change', { currentUrl, url });
    setCurrentUrl(url);
  };

  useEffect(() => {
    if (!callRejoin) return;

    const lastSessionId = localStorage.getItem(LAST_SESSION);
    if (!lastSessionId) return;
    setRejoinSessionId(lastSessionId);
  }, []);

  useEffect(() => {
    if (!rejoinSessionId) return;

    const isActiveSessionInterval = setInterval(() => {
      isActiveSession(rejoinSessionId).then((isActive) => {
        if (!isActive) {
          localStorage.removeItem(LAST_SESSION);
          setRejoinSessionId(undefined);
          clearInterval(isActiveSessionInterval);
        }
        setActiveLastSession(isActive);
      });
    }, 1000);

    return () => {
      if (isActiveSessionInterval) clearInterval(isActiveSessionInterval);
    };
  }, [rejoinSessionId]);

  useEffect(() => {
    localStorage.setItem('serverUrl', config.api.url);
    if (getParameterByName(DIRECT_CALL, currentUrl) === 'enabled') {
      setStoreOpen(true);
      if (isSdkMode()) {
        showWidget();
      }
      setAgent(getParameterByName('agent', currentUrl) ?? undefined);

      setDirectCallEnabled(true);

      void loadProductDetails();
    } else {
      void loadDataOnlyifValid();
    }
  }, [currentUrl]);

  useEffect(() => {
    const isCallForwarding = sessionStorage.getItem(FORWARDING);
    if (isCallForwarding === 'true') {
      sessionStorage.setItem(FORWARDING, String(false));
      setForwarding(true);
    }
  }, []);

  useEffect(() => {
    if (isSdkMode()) {
      hideWidget();
    }
  }, [currentUrl]);

  useEffect(() => {
    const _selectedTeam = getParameterByName('team');
    setTeam(_selectedTeam ? _selectedTeam : 'default');
  }, [currentUrl]);

  useEffect(() => {
    setChatWindowOpenStyle(
      widgetOption?.callWindowType === 'WINDOW' ? ChatWindowOpenStyle.WINDOW : ChatWindowOpenStyle.MODEL
    );
  }, [widgetOption?.callWindowType]);

  useEffect(() => {
    if (!productType) return;

    const productWidgetOption = categoryWidgetOptions?.find(({ category }) =>
      filterByProductType(category, productType)
    );
    if (productWidgetOption) {
      console.debug({
        category: productType,
        message: 'category widget option found',
        productWidgetOption
      });
      setWidgetOption(productWidgetOption.widgetOption);
    }

    const productSplashScreen = categorySplashScreens?.find(({ category }) =>
      filterByProductType(category, productType)
    );
    if (productSplashScreen) {
      console.debug({
        category: productType,
        message: 'category splash screen found',
        productSplashScreen
      });
      setSplashScreen(productSplashScreen.splashScreen);
    }

    const productMenu = categoryMenus?.find(({ category }) => filterByProductType(category, productType));
    if (productMenu) {
      console.debug({
        category: productType,
        message: 'category menu screen found',
        productMenu
      });
      setMenu(productMenu.menu);
    }
  }, [productType, currentUrl]);

  const loadDataOnlyifValid = async () => {
    const isValidWorkingHours = validateWorkingHours(workingHours);
    if (isValidWorkingHours) {
      await loadProductDetails();
      setStoreOpen(true);
    } else {
      // @ts-ignore
      console.error('Shop is closed');
    }
  };

  const loadProductDetails = async () => {
    const { product, productUrl, productType } = await loadInitialProduct({
      connection,
      currency,
      currencyConversionRate,
      productConf,
      storeId: id
    });

    if (product && productUrl) {
      setProduct(product);
      setProductUrl(productUrl);
    }

    if (productType) {
      setProductType(productType);
    }
  };

  const initialValue = useMemo(
    () => ({
      agent,
      chatWindowOpenStyle,
      currentUrl,
      directCallEnabled,
      forwarding,
      isActiveLastSession,
      isStoreOpen,
      product,
      productType,
      productUrl,
      rejoinSessionId: rejoinSessionId,
      setDirectCallEnabled,
      setForwarding,
      setProduct,
      team
    }),
    [
      forwarding,
      agent,
      chatWindowOpenStyle,
      currentUrl,
      directCallEnabled,
      isActiveLastSession,
      isStoreOpen,
      product,
      productUrl,
      rejoinSessionId,
      setDirectCallEnabled,
      team,
      setForwarding,
      productType,
      setProduct
    ]
  );

  return <CallContext.Provider value={initialValue}>{children}</CallContext.Provider>;
};

export const useCall = (): ICallContext => {
  const {
    forwarding,
    product,
    directCallEnabled,
    productUrl,
    agent,
    isStoreOpen,
    team,
    chatWindowOpenStyle,
    setDirectCallEnabled,
    rejoinSessionId,
    isActiveLastSession,
    setForwarding,
    productType,
    currentUrl,
    setProduct
  } = useContext(CallContext);
  return {
    agent,
    chatWindowOpenStyle,
    currentUrl,
    directCallEnabled,
    forwarding,
    isActiveLastSession,
    isStoreOpen,
    product,
    productType,
    productUrl,
    rejoinSessionId,
    setDirectCallEnabled,
    setForwarding,
    setProduct,
    team
  };
};

export default CallContextProvider;
